import React from 'react';
import { motion } from 'framer-motion';
import { Github, Linkedin, Mail } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { SEO } from '../components/SEO';

// Import team member images
import mohammedImage from '../assets/team/mohammed.jpeg';
import khaireddinImage from '../assets/team/khaireddin.jpeg';
import anasImage from '../assets/team/anas.jpg';
import ibrahimImage from '../assets/team/ibrahim.jpg';

interface TeamMember {
  id: number;
  name: string;
  role: string;
  image: string;
  github?: string;
  linkedin?: string;
  email?: string;
}

export default function About() {
  const { t } = useLanguage();

  const team: TeamMember[] = [
    {
      id: 1,
      name: "Mohammed Kadi",
      role: "Software Engineer",
      image: mohammedImage,
      github: "https://github.com/mohammedkadi",
      linkedin: "https://linkedin.com/in/mohammedkadi",
      email: "mohammed@lyfy.ltd"
    },
    {
      id: 2,
      name: "Khaireddine Dalaa",
      role: "Software Engineer",
      image: khaireddinImage,
      github: "https://github.com/khaireddinedalaa",
      linkedin: "https://linkedin.com/in/khaireddinedalaa",
      email: "khaireddine@lyfy.ltd"
    },
    {
      id: 3,
      name: "Anas Mrah",
      role: "3D Artist / Motion Designer",
      image: anasImage,
      linkedin: "https://linkedin.com/in/anasmrah",
      email: "anas@lyfy.ltd"
    },
    {
      id: 4,
      name: "Ibrahim AL Sadougui",
      role: "3D Artist",
      image: ibrahimImage,
      linkedin: "https://linkedin.com/in/ibrahimalsadougui",
      email: "ibrahim@lyfy.ltd"
    }
  ];

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    "mainEntity": {
      "@type": "Organization",
      "name": "LYFY",
      "description": "LYFY comprises highly motivated professionals committed to achieving outstanding outcomes.",
      "employee": team.map(member => ({
        "@type": "Person",
        "name": member.name,
        "jobTitle": member.role,
        "image": member.image,
        "sameAs": [
          member.github,
          member.linkedin
        ].filter(Boolean)
      }))
    }
  };

  return (
    <>
      <SEO 
        title="About Us"
        description="Meet the talented team behind LYFY - a group of highly motivated professionals committed to delivering outstanding software solutions and innovative designs."
        keywords="LYFY team, software engineers, 3D artists, motion designers, tech company, innovative solutions"
        canonical="https://lyfy.ltd/about"
        structuredData={structuredData}
      />
      <div className="min-h-screen bg-white py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h1 className="text-4xl font-extrabold text-gray-900 sm:text-5xl sm:tracking-tight lg:text-6xl">
              {t('about.title')}
            </h1>
            <p className="mt-5 max-w-xl mx-auto text-xl text-gray-500">
              {t('about.description')}
            </p>
          </motion.div>

          <div className="mt-10">
            <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-4">
              {team.map((member) => (
                <motion.div
                  key={member.id}
                  className="relative group"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: member.id * 0.1 }}
                >
                  <div className="relative aspect-square overflow-hidden rounded-2xl bg-white">
                    {/* Blue tint overlay */}
                    <div className="absolute inset-0 bg-[#512BD4] mix-blend-color opacity-40" />
                    
                    {/* Image */}
                    <img 
                      src={member.image} 
                      alt={member.name}
                      className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                    />
                    
                    {/* Gradient overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-gray-900/80 via-gray-900/40 to-transparent" />
                    
                    {/* Content */}
                    <div className="absolute bottom-0 left-0 right-0 p-6">
                      <div className="translate-y-8 transform opacity-0 transition-all duration-300 group-hover:translate-y-0 group-hover:opacity-100">
                        <div className="flex justify-between items-end">
                          <div>
                            <p className="text-xl font-bold text-white">{member.name}</p>
                            <p className="text-sm text-gray-300">{member.role}</p>
                          </div>
                          <div className="flex space-x-3">
                            {member.github && (
                              <a
                                href={member.github}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-white hover:text-[#512BD4] transition-colors"
                              >
                                <Github className="w-5 h-5" />
                              </a>
                            )}
                            {member.linkedin && (
                              <a
                                href={member.linkedin}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-white hover:text-[#512BD4] transition-colors"
                              >
                                <Linkedin className="w-5 h-5" />
                              </a>
                            )}
                            {member.email && (
                              <a
                                href={`mailto:${member.email}`}
                                className="text-white hover:text-[#512BD4] transition-colors"
                              >
                                <Mail className="w-5 h-5" />
                              </a>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>

          <motion.div
            className="mt-16 bg-gray-50 rounded-2xl p-8"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              {t('about.whatIsLyfy')}
            </h2>
            <p className="text-gray-700 leading-relaxed">
              LYFY comprises highly motivated professionals who are committed to achieving outstanding outcomes. 
              With a wide range of skills and a focus on client success, we collaborate closely to deliver 
              customized and innovative solutions. Our unwavering dedication, customer-centric approach, and 
              relentless pursuit of excellence position us as your trusted partner in driving your organization 
              towards success.
            </p>
          </motion.div>

          <div className="mt-10 flex justify-center space-x-6">
            <motion.a
              href="#"
              className="text-gray-400 hover:text-gray-500"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              <button className="px-6 py-3 bg-[#512BD4] text-white rounded-lg hover:bg-[#6B42E7] transition-colors">
                Book a demo
              </button>
            </motion.a>
            <motion.a
              href="#"
              className="text-gray-400 hover:text-gray-500"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              <button className="px-6 py-3 border-2 border-[#512BD4] text-[#512BD4] rounded-lg hover:bg-[#512BD4] hover:text-white transition-colors">
                Start your free trial
              </button>
            </motion.a>
          </div>
        </div>
      </div>
    </>
  );
} 